import { useCallback, useEffect, useRef, useState } from 'react';
import { Buildings, MagnifyingGlass } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi } from './mfgApi';

const LIMIT = 50;

// 品目の選択口。殻は共通の `MasterItemPicker` ひとつ（標準ルール §7.1 #3）。
// 行の中に置くので、ボタン → ダイアログの形はそのまま残している。
// props: value, onChange(id,item), excludeId, allowAllDepartments, initialItems, testid, placeholderKey
export default function ItemPickerField({
  value, onChange, excludeId = null, excludeIds = [], allowAllDepartments = false, initialItems = [],
  defaultAllMode = false,
  testid = 'item-picker', placeholderKey = 'mfg.wo.selectProduct',
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [allMode, setAllMode] = useState(!!defaultAllMode);
  const [picked, setPicked] = useState(null);
  const cacheRef = useRef(new Map()); // id -> item（ラベル解決を即時化）

  useEffect(() => { (initialItems || []).forEach((i) => cacheRef.current.set(i.id, i)); }, [initialItems]);

  // 選択中の品目ラベルは value を単一の真実の情報源として解決
  useEffect(() => {
    if (!value) { setPicked(null); return undefined; }
    const cached = cacheRef.current.get(value);
    if (cached) { setPicked(cached); return undefined; }
    let active = true;
    mfgApi.item(value).then((d) => { if (active) { cacheRef.current.set(value, d); setPicked(d); } }).catch(() => {});
    return () => { active = false; };
  }, [value, initialItems]);

  // 既に選んだ部品（と親品目）は候補から外す。現在の選択は残す。
  const excludedIds = new Set([...(excludeIds || []).filter(Boolean), ...(excludeId ? [excludeId] : [])]);

  const search = useCallback(async (q) => {
    const params = { limit: LIMIT };
    if (q) params.q = q;
    if (allMode) params.all_departments = true;
    const rows = await mfgApi.items(params);
    return (rows || [])
      .filter((i) => i.id === value || !excludedIds.has(i.id))
      .map((i) => {
        cacheRef.current.set(i.id, i);
        return { ...i, source: 'mfg', source_label: i.category || '', on_hand: i.on_hand ?? 0 };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMode, value, excludeId, (excludeIds || []).join(',')]);

  const pick = (item) => {
    cacheRef.current.set(item.id, item);
    onChange(item.id, item);
    setOpen(false);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-trigger`}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring hover:bg-zinc-50">
        <span className={`truncate ${picked ? 'text-zinc-900' : 'text-zinc-400'}`}>
          {picked
            ? <>{picked.name}{picked.model_no ? <span className="ml-1.5 text-xs text-orange-600">{picked.model_no}</span> : (picked.sku ? <span className="ml-1.5 font-mono text-xs text-zinc-400">{picked.sku}</span> : null)}</>
            : t(placeholderKey)}
        </span>
        <MagnifyingGlass size={14} className="text-zinc-400 shrink-0" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" aria-describedby={undefined} data-testid={`${testid}-modal`}>
          <DialogTitle className="text-base">{t('mfg.itemPicker.searchTitle')}</DialogTitle>
          <MasterItemPicker
            testIdPrefix={testid}
            label={t('mfg.itemPicker.searchTitle')}
            placeholder={t('mfg.itemPicker.searchPh')}
            helper={t('mfg.itemPicker.tip')}
            search={search}
            sources={['mfg']}
            minChars={0}
            onPick={pick}
            statusSlot={allowAllDepartments ? (
              <button type="button" onClick={() => setAllMode((v) => !v)} data-testid={`${testid}-alldept-toggle`}
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${allMode ? 'bg-orange-500 text-white' : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'}`}>
                <Buildings size={12} weight={allMode ? 'fill' : 'regular'} />
                {allMode ? t('mfg.itemPicker.allDepts') : t('mfg.itemPicker.deptOnly')}
              </button>
            ) : null}
            renderResultExtra={(i) => (
              <span className="tabular-nums text-[11px] text-zinc-500">
                {i.on_hand ?? 0}{i.uom || ''}
              </span>
            )}
            emptyText={t('mfg.itemPicker.noMatch')}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
